/**
 * @fileoverview Crochet accessory card: opens inline detail. Requires `[data-ctp-popup-shell]` inside
 * `.ctp-product-detail__body` (see `sections/custom-tab-product-selector.liquid`); fragments from
 * `<template>` in the same `.ctp-product-root` fill `data-ctp-dynamic` slots.
 * Pairing: `data-template-id` on `<custom-quick-add>` (`snippets/custom-crochet-list.liquid`) must match
 * `<template id="…">` from `snippets/custom-crochet-detail-popup.liquid` (same `forloop.index` over `crochet_products`).
 */

const SELECTOR_ROOT = '.ctp-product-root';
const SELECTOR_MAIN = '.ctp-product';
const SELECTOR_DETAIL = '.ctp-product-detail';
const SELECTOR_DETAIL_BODY = '.ctp-product-detail__body';
const SELECTOR_POPUP_SHELL = '[data-ctp-popup-shell]';
const SELECTOR_DYNAMIC_MEDIA = '[data-ctp-dynamic="media"]';
const SELECTOR_DYNAMIC_MAIN = '[data-ctp-dynamic="main"]';
const SELECTOR_DETAIL_CARDS = '[data-ctp-detail-cards]';
const SELECTOR_PRODUCT_FRAGMENT = '[data-ctp-product-fragment]';
const SELECTOR_FRAGMENT_MEDIA = '[data-ctp-fragment-media]';
const SELECTOR_FRAGMENT_MAIN = '[data-ctp-fragment-main]';
/** Dialog title in popup shell (`sections/custom-tab-product-selector.liquid`); used for `aria-labelledby`. */
const SELECTOR_QA_HEADING = '[data-ctp-qa-heading]';
const SELECTOR_DETAIL_CLOSE = '.ctp-product-detail__close';
/** Pairs with `custom-tab-product-selector.css` (`ctp-product-detail-fade-in`). Applied only to `data-ctp-dynamic` slots. */
const CLASS_POPUP_CONTENT_FADE = 'ctp-popup-content--fade-in';
const CLASS_SHOWING_DETAIL = 'is-showing-detail';
/** Fade-out in progress before `display: none` on `.ctp-product-detail`. */
const CLASS_CLOSING_DETAIL = 'is-closing-detail';

/**
 * Timer + `animationend` listener while closing (cleared on tab switch, re-open, or completion).
 *
 * @type {WeakMap<HTMLElement, { timerId: ReturnType<typeof setTimeout>, onEnd: (e: AnimationEvent) => void }>}
 */
const closeAnimCleanupByRoot = new WeakMap();
/** Applied to `li` wrapping the active crochet card (main grid + popup grid). */
const CLASS_CROCHET_SELECTED = 'ctp-crochet-card--selected';

/** Namespace for variant listeners on CTP quick-add (paired with Element.prototype.on). */
const CTP_VARIANT_EVENT_NS = '.ctpQuickAdd';

const SELECTOR_CTP_CROCHET_BUNDLE = '[data-ctp-crochet-bundle]';

/**
 * @returns {string}
 */
function newCtpBundleId() {
    return window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * POST `/cart/add.js` with `{ items }` — same contract as `addon-product.js` (properties `_bundle_id`, etc.).
 *
 * @param {Array<{ id: number, quantity: number, properties: Record<string, string> }>} items
 * @param {{ form: HTMLFormElement, addToCartBtn: Element | null }} ctx
 * @returns {Promise<void>}
 */
async function postCtpCartItemsJson(items, {form, addToCartBtn}) {
    const route = theme?.routes?.cartAdd;
    if (!route) return;

    addToCartBtn?.classList.add('btn--loading');
    try {
        const res = await fetch(route, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({items}),
        });
        const data = await res.json();

        if (data.status === 422 || data.errors) {
            const msg =
                (typeof data.description === 'string' && data.description) ||
                (typeof data.message === 'string' && data.message) ||
                (typeof data.errors === 'string' && data.errors) ||
                'Could not add to cart';
            window.alert(msg);
            return;
        }

        if (theme.settings?.cartType === 'page') {
            window.location.href = theme.routes.cartPage;
            return;
        }

        form.dispatchEvent(
            new CustomEvent('ajaxProduct:added', {
                detail: {product: data, addToCartBtn},
                bubbles: true,
            }),
        );
    } catch {
        window.alert('Could not add to cart');
    } finally {
        addToCartBtn?.classList.remove('btn--loading');
    }
}

/**
 * Tab product + crochet: reads `[data-ctp-crochet-bundle]` JSON from Liquid; otherwise defers to `AjaxProduct`.
 *
 * @param {HTMLFormElement} form
 * @param {HTMLElement} holder
 * @returns {void}
 */
function wireCtpProductFormSubmit(form, holder) {
    if (typeof theme === 'undefined' || !theme.AjaxProduct) return;

    const raw = holder.querySelector(SELECTOR_CTP_CROCHET_BUNDLE)?.textContent?.trim();
    /** @type {{ crochetVariantId?: number, title?: string, image?: string | null, parentProductUrl?: string } | null} */
    let crochet = null;
    if (raw) {
        try {
            crochet = JSON.parse(raw);
        } catch {
            crochet = null;
        }
    }
    if (!crochet?.crochetVariantId) {
        new theme.AjaxProduct(form);
        return;
    }

    const addToCartBtn = form.querySelector('[data-add-to-cart]');
    let busy = false;

    form.addEventListener(
        'submit',
        async evt => {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            if (addToCartBtn?.disabled) return;
            if (busy || !theme.routes?.cartAdd) return;

            const mainId = parseInt(String(form.querySelector('[name="id"]')?.value || ''), 10);
            const crochetId = parseInt(String(crochet.crochetVariantId), 10);
            const qty = parseInt(String(form.querySelector('[name="quantity"]')?.value || '1'), 10) || 1;
            if (!mainId || Number.isNaN(crochetId)) return;

            const bundleId = newCtpBundleId();
            const title = typeof crochet.title === 'string' ? crochet.title : '';
            const propsMain = {Addon: title, _bundle_id: bundleId};
            if (crochet.image) propsMain._addon_image = String(crochet.image);
            const parentProductUrl =
                typeof crochet.parentProductUrl === 'string' ? crochet.parentProductUrl.trim() : '';
            const childProps = {_addon_bundle_child: 'true', _bundle_id: bundleId};
            if (parentProductUrl) {
                childProps._addon_parent_url = parentProductUrl;
            }

            busy = true;
            await postCtpCartItemsJson(
                [
                    {id: mainId, quantity: qty, properties: propsMain},
                    {
                        id: crochetId,
                        quantity: qty,
                        properties: childProps,
                    },
                ],
                {form, addToCartBtn},
            );
            busy = false;
        },
        true,
    );
}

/**
 * Enables variant radios/selects + master select sync, AJAX add-to-cart, and live price in a cloned quick-add panel.
 *
 * @param {HTMLElement} bodySlot - `.ctp-product-detail__body` containing the cloned template.
 * @returns {void}
 */
function initCtpQuickAddCommerce(bodySlot) {
    const holder = bodySlot.querySelector('[data-ctp-quick-add-product]');
    if (!holder) return;

    const form = holder.querySelector('form.product-single__form');
    if (form) {
        wireCtpProductFormSubmit(form, holder);
    }

    const variantJsonEl = holder.querySelector('[data-variant-json]');
    if (!variantJsonEl || typeof theme === 'undefined' || !theme.Variants) {
        return;
    }

    let variants;
    try {
        variants = JSON.parse(variantJsonEl.textContent || '[]');
    } catch {
        return;
    }

    if (!Array.isArray(variants) || variants.length === 0) return;

    const dynamicVariantsEnabled = Boolean(holder.querySelector('[data-dynamic-variants-enabled]'));

    new theme.Variants({
        container: holder,
        enableHistoryState: false,
        singleOptionSelector: '[data-variant-input]',
        originalSelectorId: '[data-product-select]',
        variants,
        dynamicVariantsEnabled,
    });

    const basePriceEl = holder.querySelector('[data-ctp-base-price]');
    const moneyFormat = theme.settings?.moneyFormat;

    /** @param {Event} evt */
    const onVariantChange = evt => {
        const variant = evt.detail?.variant;
        const cartBtn = holder.querySelector('[data-add-to-cart]');
        const cartBtnText = holder.querySelector('[data-add-to-cart-text]');

        if (basePriceEl && variant && theme.Currency && moneyFormat) {
            basePriceEl.textContent = theme.Currency.formatMoney(variant.price, moneyFormat);
        }

        if (!cartBtn || !cartBtnText) return;

        if (variant) {
            if (variant.available && Number(variant.price) > 0) {
                cartBtn.classList.remove('disabled');
                cartBtn.disabled = false;
                const defaultText = cartBtnText.dataset.defaultText;
                if (defaultText) cartBtnText.textContent = defaultText;
            } else {
                cartBtn.classList.add('disabled');
                cartBtn.disabled = true;
                if (Number(variant.price) <= 0 && theme.strings?.unavailable) {
                    cartBtnText.textContent = theme.strings.unavailable;
                } else if (theme.strings?.soldOut) {
                    cartBtnText.textContent = theme.strings.soldOut;
                }
            }
        } else {
            cartBtn.classList.add('disabled');
            cartBtn.disabled = true;
            if (theme.strings?.unavailable) cartBtnText.textContent = theme.strings.unavailable;
        }
    };

    holder.on(`variantChange${CTP_VARIANT_EVENT_NS}`, onVariantChange);
}

/** @type {WeakMap<Element, HTMLElement>} */
const openTriggerByRoot = new WeakMap();

/** @type {WeakMap<Element, function(Event): void>} */
const escapeHandlerByRoot = new WeakMap();

/** @type {WeakSet<Element>} */
const rootsCloseWired = new WeakSet();

/**
 * Moves all child nodes from `source` into `target` (empties `source`).
 *
 * @param {HTMLElement} target
 * @param {HTMLElement | null} source
 * @returns {void}
 */
function ctpMoveChildNodes(target, source) {
    if (!source) return;
    while (source.firstChild) {
        target.appendChild(source.firstChild);
    }
}

/**
 * Clears injected media/main nodes from the popup shell (see `closeDetail`).
 *
 * @param {HTMLElement} shell - `[data-ctp-popup-shell]`
 * @returns {void}
 */
function clearCtpPopupShellSlots(shell) {
    shell.querySelector(SELECTOR_DYNAMIC_MEDIA)?.replaceChildren();
    shell.querySelector(SELECTOR_DYNAMIC_MAIN)?.replaceChildren();
}

/**
 * Fade-in only injected slots when switching add-on with the dialog already open.
 *
 * @param {HTMLElement} mediaSlot
 * @param {HTMLElement} mainSlot
 * @returns {void}
 */
function runCtpDynamicSlotsFade(mediaSlot, mainSlot) {
    mediaSlot.classList.remove(CLASS_POPUP_CONTENT_FADE);
    mainSlot.classList.remove(CLASS_POPUP_CONTENT_FADE);
    void mediaSlot.offsetWidth;

    /** @param {HTMLElement} slot */
    const onSlotAnimationEnd = slot => {
        /** @param {AnimationEvent} e */
        const handler = e => {
            if (e.target !== slot) return;
            slot.classList.remove(CLASS_POPUP_CONTENT_FADE);
            slot.removeEventListener('animationend', handler);
        };
        return handler;
    };

    mediaSlot.addEventListener('animationend', onSlotAnimationEnd(mediaSlot));
    mainSlot.addEventListener('animationend', onSlotAnimationEnd(mainSlot));

    mediaSlot.classList.add(CLASS_POPUP_CONTENT_FADE);
    mainSlot.classList.add(CLASS_POPUP_CONTENT_FADE);
}

/**
 * Stops close fade and drops listeners (user picked another add-on while closing).
 *
 * @param {HTMLElement} root
 * @returns {void}
 */
function abortCtpCloseAnimation(root) {
    const cleanup = closeAnimCleanupByRoot.get(root);
    if (cleanup) {
        clearTimeout(cleanup.timerId);
        const detailEl = root.querySelector(SELECTOR_DETAIL);
        if (detailEl) detailEl.removeEventListener('animationend', cleanup.onEnd);
        closeAnimCleanupByRoot.delete(root);
    }
    root.classList.remove(CLASS_CLOSING_DETAIL);
}

class CustomQuickAdd extends HTMLElement {
    /** @type {AbortController | null} */
    #abort = null;

    connectedCallback() {
        const templateId = this.dataset.templateId?.trim();
        if (!templateId) return;

        if (this.#abort) return;

        this.#abort = new AbortController();
        const {signal} = this.#abort;

        this.addEventListener('click', this.#onClick, {signal});
        this.addEventListener('keydown', this.#onKeydown, {signal});
    }

    disconnectedCallback() {
        this.#abort?.abort();
        this.#abort = null;
    }

    /**
     * DOM teardown after close animation (or immediate close).
     *
     * @param {HTMLElement} root
     * @returns {void}
     */
    static #finishCloseDetail(root) {
        const main = root.querySelector(SELECTOR_MAIN);
        const detail = root.querySelector(SELECTOR_DETAIL);
        const body = root.querySelector(SELECTOR_DETAIL_BODY);

        if (body) {
            const shell = body.querySelector(SELECTOR_POPUP_SHELL);
            if (shell) clearCtpPopupShellSlots(shell);
        }

        root.classList.remove(CLASS_SHOWING_DETAIL, CLASS_CLOSING_DETAIL);

        if (main) {
            main.removeAttribute('aria-hidden');
            if ('inert' in main) main.inert = false;
        }

        if (detail) {
            detail.setAttribute('aria-hidden', 'true');
            detail.removeAttribute('aria-labelledby');
            detail.removeAttribute('aria-label');
        }

        root.querySelectorAll('custom-quick-add[aria-expanded="true"]').forEach(el => {
            el.setAttribute('aria-expanded', 'false');
        });

        root.querySelectorAll('custom-quick-add').forEach(el => {
            el?.classList.remove(CLASS_CROCHET_SELECTED);
        });

        const trigger = openTriggerByRoot.get(root);
        openTriggerByRoot.delete(root);
        if (trigger?.isConnected) trigger.focus();
    }

    /**
     * @param {HTMLElement} root
     */
    static closeDetail(root) {
        if (!(root instanceof HTMLElement)) return;
        if (!root.classList.contains(CLASS_SHOWING_DETAIL)) return;

        const detail = root.querySelector(SELECTOR_DETAIL);

        if (root.classList.contains(CLASS_CLOSING_DETAIL)) {
            const cleanup = closeAnimCleanupByRoot.get(root);
            if (cleanup) {
                clearTimeout(cleanup.timerId);
                if (detail) detail.removeEventListener('animationend', cleanup.onEnd);
                closeAnimCleanupByRoot.delete(root);
            }
            root.classList.remove(CLASS_CLOSING_DETAIL);
            CustomQuickAdd.#finishCloseDetail(root);
            return;
        }

        const escHandler = escapeHandlerByRoot.get(root);
        if (escHandler) {
            document.removeEventListener('keydown', escHandler);
            escapeHandlerByRoot.delete(root);
        }

        let completed = false;
        const finishOnce = () => {
            if (completed) return;
            completed = true;
            const cleanup = closeAnimCleanupByRoot.get(root);
            if (cleanup) {
                clearTimeout(cleanup.timerId);
                if (detail) detail.removeEventListener('animationend', cleanup.onEnd);
                closeAnimCleanupByRoot.delete(root);
            }
            CustomQuickAdd.#finishCloseDetail(root);
        };

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduceMotion || !(detail instanceof HTMLElement)) {
            finishOnce();
            return;
        }

        root.classList.add(CLASS_CLOSING_DETAIL);

        /** @param {AnimationEvent} e */
        const onEnd = e => {
            if (e.target !== detail) return;
            finishOnce();
        };

        const timerId = window.setTimeout(finishOnce, 340);
        closeAnimCleanupByRoot.set(root, {timerId, onEnd});
        detail.addEventListener('animationend', onEnd);
    }

    /**
     * Close control lives inside each cloned `<template>`; one delegated listener per root.
     *
     * @param {HTMLElement} root
     */
    #wireDetailClickDelegation(root) {
        const detail = root.querySelector(SELECTOR_DETAIL);
        if (!detail || rootsCloseWired.has(root)) return;
        rootsCloseWired.add(root);
        detail.addEventListener('click', e => {
            const t = e.target;
            if (!(t instanceof Element)) return;
            const close = t.closest(SELECTOR_DETAIL_CLOSE);
            if (close && detail.contains(close)) CustomQuickAdd.closeDetail(root);
        });
    }

    /**
     * @param {HTMLElement} root
     * @param {string} id
     * @returns {HTMLTemplateElement | null}
     */
    static #getTemplateById(root, id) {
        if (!id) return null;
        try {
            const el = root.querySelector(`#${CSS.escape(id)}`);
            return el instanceof HTMLTemplateElement ? el : null;
        } catch {
            return null;
        }
    }

    #openInline() {
        const root = this.closest(SELECTOR_ROOT);
        const templateId = this.dataset.templateId?.trim() ?? '';
        const tpl = CustomQuickAdd.#getTemplateById(root, templateId);
        const main = root?.querySelector(SELECTOR_MAIN);
        const detail = root?.querySelector(SELECTOR_DETAIL);
        const bodySlot = root?.querySelector(SELECTOR_DETAIL_BODY);

        if (!root || !tpl || !main || !detail || !bodySlot) return;

        abortCtpCloseAnimation(root);

        const popupWasOpen = root.classList.contains(CLASS_SHOWING_DETAIL);

        const shell = bodySlot.querySelector(SELECTOR_POPUP_SHELL);
        if (!shell) return;

        const mediaSlot = shell.querySelector(SELECTOR_DYNAMIC_MEDIA);
        const mainSlot = shell.querySelector(SELECTOR_DYNAMIC_MAIN);
        if (!mediaSlot || !mainSlot) return;

        this.#wireDetailClickDelegation(root);

        const clone = tpl.content.cloneNode(true);
        const fragRoot = clone.querySelector(SELECTOR_PRODUCT_FRAGMENT);
        if (!fragRoot) return;

        const mediaSrc = fragRoot.querySelector(SELECTOR_FRAGMENT_MEDIA);
        const mainSrc = fragRoot.querySelector(SELECTOR_FRAGMENT_MAIN);
        mediaSlot.replaceChildren();
        mainSlot.replaceChildren();
        ctpMoveChildNodes(mediaSlot, mediaSrc);
        ctpMoveChildNodes(mainSlot, mainSrc);

        initCtpQuickAddCommerce(bodySlot);

        if (popupWasOpen && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            runCtpDynamicSlotsFade(mediaSlot, mainSlot);
        }

        const label = this.dataset.detailLabel;
        if (label) detail.setAttribute('aria-label', label);

        const headingEl = bodySlot.querySelector(SELECTOR_QA_HEADING);
        if (headingEl?.id) {
            detail.setAttribute('aria-labelledby', headingEl.id);
        } else {
            detail.removeAttribute('aria-labelledby');
        }

        main.setAttribute('aria-hidden', 'true');
        if ('inert' in main) main.inert = true;

        detail.setAttribute('aria-hidden', 'false');

        root.querySelectorAll('custom-quick-add').forEach(el => {
            el.setAttribute('aria-expanded', 'false');
            el?.classList.remove(CLASS_CROCHET_SELECTED);
        });

        /** @type {HTMLElement | null} */
        let matchingMain = null;
        if (templateId) {
            matchingMain = main.querySelector(`custom-quick-add[data-template-id="${CSS.escape(templateId)}"]`) ?? null;
        }

        /** @type {HTMLElement | null} */
        let matchingBody = null;
        if (templateId) {
            try {
                matchingBody =
                    bodySlot.querySelector(
                        `${SELECTOR_DETAIL_CARDS} custom-quick-add[data-template-id="${CSS.escape(templateId)}"]`,
                    ) ?? null;
            } catch {
                matchingBody = null;
            }
        }

        matchingMain?.setAttribute('aria-expanded', 'true');
        matchingBody?.setAttribute('aria-expanded', 'true');

        matchingMain?.classList.add(CLASS_CROCHET_SELECTED);
        matchingBody?.classList.add(CLASS_CROCHET_SELECTED);

        openTriggerByRoot.set(root, matchingMain ?? matchingBody ?? this);

        const prevEsc = escapeHandlerByRoot.get(root);
        if (prevEsc) document.removeEventListener('keydown', prevEsc);

        const onEscape = e => {
            if (e.key !== 'Escape' || !root.classList.contains(CLASS_SHOWING_DETAIL)) return;
            if (root.classList.contains(CLASS_CLOSING_DETAIL)) return;
            e.preventDefault();
            CustomQuickAdd.closeDetail(root);
        };
        escapeHandlerByRoot.set(root, onEscape);
        document.addEventListener('keydown', onEscape);

        root.classList.add(CLASS_SHOWING_DETAIL);

        requestAnimationFrame(() => {
            shell.querySelector(SELECTOR_DETAIL_CLOSE)?.focus();
        });
    }

    #onClick = event => {
        if (event.target.closest('a')) return;
        this.#openInline();
    };

    #onKeydown = event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.#openInline();
    };
}

if (!customElements.get('custom-quick-add')) {
    customElements.define('custom-quick-add', CustomQuickAdd);
}

/** Lets the tab selector close an open inline detail when switching tabs (separate script). */
window.__ctpCloseProductDetail = root => CustomQuickAdd.closeDetail(root);
